import { ChangeEvent, FC, useState } from 'react';
import {
  Flex,
  FormControl,
  FormLabel,
  Icon,
  IconButton,
  NumberInput,
  NumberInputField,
  Select,
  SimpleGrid,
  Text,
} from '@chakra-ui/react';
import { HiOutlineSparkles } from 'react-icons/hi2';
import { LuTrash2 } from 'react-icons/lu';
import type { AnimationEffect } from '@adflow/types';
import theme from '@adflow/theme';
import { NAMED_EASINGS, isNamedEasing, type NamedEasing } from '@adflow/utils';
import { getAnimationPreset, resolvePresetParams } from '../../../animation';

const EASING_LABELS: Record<NamedEasing, string> = {
  linear: 'Linear',
  'ease-in': 'Ease In',
  'ease-out': 'Ease Out',
  'ease-in-out': 'Ease In Out',
};

const fieldLabelStyles = {
  fontSize: 'xs',
  fontWeight: 'medium',
  color: 'gray.500',
  mb: 1,
};

type NumberParamFieldProps = {
  label: string;
  value: number;
  min: number;
  step: number;
  precision: number;
  testId: string;
  onCommit: (value: number) => void;
};

const NumberParamField: FC<NumberParamFieldProps> = ({
  label,
  value,
  min,
  step,
  precision,
  testId,
  onCommit,
}) => {
  // Keep the raw text while the field is being edited so transient states like
  // "" or "0." are not snapped back to the committed value on every keystroke.
  // Only finite, in-range values are committed.
  const [draft, setDraft] = useState<string | null>(null);

  const handleChange = (valueAsString: string, valueAsNumber: number) => {
    setDraft(valueAsString);
    if (Number.isFinite(valueAsNumber) && valueAsNumber >= min && valueAsNumber !== value) {
      onCommit(valueAsNumber);
    }
  };

  return (
    <FormControl>
      <FormLabel {...fieldLabelStyles}>{label}</FormLabel>
      <NumberInput
        size="sm"
        min={min}
        step={step}
        precision={precision}
        clampValueOnBlur={false}
        value={draft ?? value}
        onChange={handleChange}
      >
        <NumberInputField data-test-id={testId} onBlur={() => setDraft(null)} />
      </NumberInput>
    </FormControl>
  );
};

type Props = {
  effect: AnimationEffect;
  onUpdate: (effectId: string, params: Record<string, number | string | boolean>) => void;
  onRemove: (effectId: string) => void;
};

export const AppliedEffectEditor: FC<Props> = ({ effect, onUpdate, onRemove }) => {
  const preset = getAnimationPreset(effect.presetId);
  const params = resolvePresetParams(preset, effect.params);
  const isSlide = preset.group === 'slide';

  const handleNumberParam = (key: 'duration' | 'start' | 'distance') => (value: number) => {
    onUpdate(effect.id, { [key]: value });
  };

  const handleEasingChange = (event: ChangeEvent<HTMLSelectElement>) => {
    if (isNamedEasing(event.target.value)) {
      onUpdate(effect.id, { easing: event.target.value });
    }
  };

  return (
    <Flex
      direction="column"
      gap={3}
      p={4}
      borderWidth="1px"
      borderColor={theme.palette.mcBlue.lighter}
      borderRadius="lg"
      bg="white"
      data-test-id={`applied-effect-${effect.id}`}
      data-applied-preset={effect.presetId}
    >
      <Flex alignItems="center" justifyContent="space-between" gap={2}>
        <Flex alignItems="center" gap={2} minW={0}>
          <Icon
            as={HiOutlineSparkles}
            boxSize={4}
            color={theme.palette.mcNavy.main}
            flexShrink={0}
          />
          <Text fontSize="sm" fontWeight="bold" color={theme.palette.mcNavy.main} noOfLines={1}>
            {preset.label}
          </Text>
        </Flex>
        <IconButton
          aria-label={`Remove ${preset.label}`}
          icon={<LuTrash2 />}
          size="sm"
          variant="ghost"
          color="gray.600"
          onClick={() => onRemove(effect.id)}
          data-test-id={`applied-effect-remove-${effect.id}`}
        />
      </Flex>
      <SimpleGrid columns={2} spacing={3}>
        <NumberParamField
          label="Duration (s)"
          value={params.duration}
          min={0}
          step={0.1}
          precision={2}
          testId={`applied-effect-duration-${effect.id}`}
          onCommit={handleNumberParam('duration')}
        />
        <NumberParamField
          label="Start (s)"
          value={params.start}
          min={0}
          step={0.1}
          precision={2}
          testId={`applied-effect-start-${effect.id}`}
          onCommit={handleNumberParam('start')}
        />
      </SimpleGrid>
      <FormControl>
        <FormLabel {...fieldLabelStyles}>Easing</FormLabel>
        <Select
          size="sm"
          value={params.easing}
          onChange={handleEasingChange}
          data-test-id={`applied-effect-easing-${effect.id}`}
        >
          {(Object.keys(NAMED_EASINGS) as NamedEasing[]).map((easing) => (
            <option key={easing} value={easing}>
              {EASING_LABELS[easing]}
            </option>
          ))}
        </Select>
      </FormControl>
      {isSlide && (
        <NumberParamField
          label="Distance (px)"
          value={params.distance ?? 0}
          min={0}
          step={1}
          precision={0}
          testId={`applied-effect-distance-${effect.id}`}
          onCommit={handleNumberParam('distance')}
        />
      )}
    </Flex>
  );
};
